import { type AxiosResponse } from 'axios'

import axiosInstance from '@/lib/axiosInstance'
import { ControllerEndpoint } from '@/services/controllerEndpoint'
import { aesService } from '@/services/aesService'
import { rsaService } from '@/services/rsaService'
import { authSessionStorageService } from '@/services/sessionStorageService'
import { encodeString } from '@/services/hexService'

import {
  type CreateNewSessionResponseModel,
  type GetSessionResponseModel,
} from './types'

// Debug flags
const DEBUG_SESSION = process.env.NODE_ENV === 'development' && false
const DEBUG_LOGIN = process.env.NODE_ENV === 'development' && false

// Log levels for production monitoring
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
} as const

const CURRENT_LOG_LEVEL = process.env.NODE_ENV === 'production' ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG

interface CreateUserRequest {
  userName: string
  password: string
  name: string
}

/**
 * Performance-optimized logging utility
 */
class AuthLogger {
  private static shouldLog(level: keyof typeof LOG_LEVELS): boolean {
    return LOG_LEVELS[level] <= CURRENT_LOG_LEVEL
  }

  static debug(message: string, data?: any, flag: boolean = DEBUG_SESSION): void {
    if (flag && this.shouldLog('DEBUG')) {
      console.log(`[AUTH DEBUG] ${message}`, data ? data : '')
    }
  }

  static info(message: string, data?: any): void {
    if (this.shouldLog('INFO')) {
      console.info(`[AUTH INFO] ${message}`, data ? data : '')
    }
  }

  static warn(message: string, data?: any): void {
    if (this.shouldLog('WARN')) {
      console.warn(`[AUTH WARN] ${message}`, data ? data : '')
    }
  }

  static error(message: string, error?: any): void {
    if (this.shouldLog('ERROR')) {
      console.error(`[AUTH ERROR] ${message}`, error ? error : '')
    }
  }

  // Session data logging with privacy protection
  static sessionData(message: string, data: any): void {
    if (DEBUG_SESSION && this.shouldLog('DEBUG')) {
      console.log(`[SESSION DEBUG] ${message}`, {
        hasSessionId: !!data.sessionId,
        hasLoginTime: !!data.loginTime,
        hasPublicKey: !!data.publicKey,
        hasSecretKey: !!data.secretKey,
      })
    }
  }

  // Login flow logging
  static loginFlow(step: string, message: string, data?: any): void {
    if (DEBUG_LOGIN && this.shouldLog('DEBUG')) {
      console.log(`[LOGIN ${step}] ${message}`, data ? data : '')
    }
  }
}

/**
 * Production-ready authentication service with comprehensive session management,
 * logging, and error handling for enterprise applications.
 */
class OptimizedAuthService {
  // Login status tracking
  loginStatus = false
  private lastIntegrityCheck = 0
  private readonly INTEGRITY_CHECK_INTERVAL = 30000 // 30 seconds
  
  // Auth state change callbacks
  private authStateCallbacks: Array<(isAuthenticated: boolean) => void> = []

  /**
   * Enhanced authentication check with session validation
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const sessionData = authSessionStorageService.getAllSessionData()
      
      AuthLogger.sessionData('Current session data', sessionData)
      
      // Check if we have both session ID and login time
      if (sessionData.sessionId && sessionData.loginTime) {
        AuthLogger.debug('Session valid - has both sessionId and loginTime')
        return true
      }
      
      // If we have a session ID but no login time, check if login just completed
      if (sessionData.sessionId && !sessionData.loginTime && this.loginStatus) {
        AuthLogger.debug('Have sessionId and loginStatus=true, checking for loginTime...')
        
        // Wait a moment for login time to be set
        await new Promise(resolve => setTimeout(resolve, 100))
        
        // Check again
        const updatedSessionData = authSessionStorageService.getAllSessionData()
        AuthLogger.sessionData('After delay, session data', updatedSessionData)
        
        if (updatedSessionData.sessionId && updatedSessionData.loginTime) {
          AuthLogger.debug('Session valid after delay')
          return true
        }
      }

      AuthLogger.debug('No valid session found')
      return false
    } catch (error) {
      AuthLogger.error('Error in isAuthenticated', error)
      return false
    }
  }

  /**
   * Enhanced login with comprehensive error handling and session management
   */
  async login(username: string, password: string, rememberMe = false): Promise<boolean> {
    const startTime = Date.now()
    AuthLogger.loginFlow('START', 'Starting login process', { username })
    
    try {
      // Ensure session exists before login
      AuthLogger.loginFlow('STEP1', 'Ensuring session exists...')
      await this.ensureSessionExists()
      
      AuthLogger.loginFlow('STEP2', 'Getting public key...')
      const storedPublicKey = authSessionStorageService.getPublicKey()
      if (!storedPublicKey) {
        throw new Error('No public key found after ensuring session')
      }
      
      AuthLogger.loginFlow('STEP3', 'Encrypting password...')
      const publicKey = await rsaService.getPublicKeyFromString(storedPublicKey)
      const loginReq = await this.encryptPasswordAndPrepareLoginReq(
        password,
        publicKey,
        username,
      )

      AuthLogger.loginFlow('STEP4', 'Sending login request...')
      const response = await axiosInstance.put(
        ControllerEndpoint.SessionControllerEndpoint.SESSION,
        { ...loginReq, rememberMe },
      )

      if (response.status === 204 || response.status === 200) {
        this.loginStatus = true
        authSessionStorageService.setLoginTime()
        
        const duration = Date.now() - startTime
        AuthLogger.loginFlow('SUCCESS', 'Login completed successfully', { 
          username, 
          duration: `${duration}ms` 
        })
        
        // Notify auth state change
        this.notifyAuthStateChange()
        return true
      } else {
        AuthLogger.loginFlow('FAILURE', 'Unexpected response status', { 
          status: response.status 
        })
        return false
      }
    } catch (error) {
      const duration = Date.now() - startTime
      AuthLogger.loginFlow('ERROR', 'Login failed', { 
        username, 
        duration: `${duration}ms`,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      
      // Handle specific error cases
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any
        if (axiosError.response?.status === 403 || axiosError.response?.status === 401) {
          this.loginStatus = false
          this.notifyAuthStateChange()
          return false
        }
      }
      
      // For other errors, clear session and re-throw
      authSessionStorageService.clearSession()
      this.loginStatus = false
      this.notifyAuthStateChange()
      throw error
    }
  }

  /**
   * PRODUCTION-READY: Create new user with comprehensive error handling
   */
  async createUser(username: string, password: string, name: string): Promise<void> {
    AuthLogger.info('Starting user creation process for:', username)
    
    try {
      // Ensure we have a valid session
      await this.ensureSessionExists()
      
      const publicKey = authSessionStorageService.getPublicKey()
      if (!publicKey) {
        throw new Error('Public key not available for encryption')
      }
      
      AuthLogger.debug('Encrypting password for user creation...', undefined, DEBUG_SESSION)
      const rsaPublicKey = await rsaService.getPublicKeyFromString(publicKey)
      const encryptedPassword = await rsaService.encrypt(password, rsaPublicKey)
      
      const createUserRequest: CreateUserRequest = {
        userName: username,
        password: encryptedPassword,
        name: name,
      }
      
      AuthLogger.debug('Sending user creation request...', undefined, DEBUG_SESSION)
      const response = await axiosInstance.post(
        ControllerEndpoint.UserControllerEndpoint.CREATEUSER,
        createUserRequest,
      )
      
      if (response.status === 201 || response.status === 200) {
        AuthLogger.info('User creation successful for:', username)
      } else {
        throw new Error(`Unexpected response status: ${response.status}`)
      }
    } catch (error) {
      AuthLogger.error('User creation failed for:', username, error)
      
      // Handle specific error cases
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any
        if (axiosError.response?.status === 409) {
          throw new Error('User already exists with this email address')
        }
        if (axiosError.response?.status === 400) {
          throw new Error('Invalid user data provided')
        }
        if (axiosError.response?.status === 403 || axiosError.response?.status === 401) {
          throw new Error('Session expired. Please refresh the page and try again.')
        }
      }
      
      // Re-throw with context
      throw error instanceof Error ? error : new Error('User creation failed')
    }
  }

  /**
   * PRODUCTION-READY: Update user password with current authentication
   */
  async updatePassword(currentPassword: string, newPassword: string): Promise<void> {
    AuthLogger.info('Starting password update process')
    
    try {
      // Ensure we have a valid session and are authenticated
      const isAuth = await this.isAuthenticated()
      if (!isAuth) {
        throw new Error('User must be authenticated to change password')
      }
      
      // Get the secret key for AES encryption (different from RSA used in signup)
      const secretKey = authSessionStorageService.getSecretKey()
      if (!secretKey) {
        throw new Error('Session secret key not available for password encryption')
      }
      
      AuthLogger.debug('Encrypting passwords with AES...')
      
      // Load the AES key and encrypt both passwords
      const aesKey = await aesService.loadSecretKey(secretKey)
      const encryptedCurrentPassword = await aesService.encrypt(currentPassword, aesKey)
      const encryptedNewPassword = await aesService.encrypt(newPassword, aesKey)
      
      const updateRequest = {
        currentPassword: encryptedCurrentPassword,
        newPassword: encryptedNewPassword,
      }
      
      AuthLogger.debug('Sending password update request...')
      const response = await axiosInstance.put(
        ControllerEndpoint.UserControllerEndpoint.PROFILE,
        updateRequest,
      )
      
      if (response.status === 202 || response.status === 200) {
        AuthLogger.info('Password updated successfully')
      } else {
        throw new Error(`Unexpected response status: ${response.status}`)
      }
    } catch (error) {
      AuthLogger.error('Password update failed', error)
      
      // Handle specific error cases
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any
        if (axiosError.response?.status === 400) {
          throw new Error('Current password is incorrect')
        }
        if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
          throw new Error('Session expired. Please log in again.')
        }
        if (axiosError.response?.status === 422) {
          throw new Error('New password does not meet security requirements')
        }
      }
      
      // Re-throw with context
      throw error instanceof Error ? error : new Error('Password update failed')
    }
  }

  /**
   * PRODUCTION-READY: Verify email OTP (for account verification)
   */
  async verifyEmailOTP(otp: string): Promise<void> {
    AuthLogger.info('Starting email OTP verification')
    
    try {
      // Ensure we have a valid session
      const isAuth = await this.isAuthenticated()
      if (!isAuth) {
        throw new Error('User must be authenticated to verify OTP')
      }
      
      const verifyRequest = {
        emailOTP: otp,
      }
      
      AuthLogger.debug('Sending OTP verification request...')
      const response = await axiosInstance.put(
        ControllerEndpoint.UserControllerEndpoint.PROFILE,
        verifyRequest,
      )
      
      if (response.status === 202 || response.status === 200) {
        AuthLogger.info('Email OTP verified successfully')
      } else {
        throw new Error(`Unexpected response status: ${response.status}`)
      }
    } catch (error) {
      AuthLogger.error('Email OTP verification failed', error)
      
      // Handle specific error cases
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any
        if (axiosError.response?.status === 404) {
          throw new Error('Invalid or expired OTP code')
        }
        if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
          throw new Error('Session expired. Please log in again.')
        }
      }
      
      // Re-throw with context
      throw error instanceof Error ? error : new Error('OTP verification failed')
    }
  }

  /**
   * Enhanced logout with proper cleanup
   */
  async logout(): Promise<void> {
    AuthLogger.info('Starting logout process...')
    
    try {
      // Attempt graceful logout with server
      await axiosInstance.delete(ControllerEndpoint.SessionControllerEndpoint.LOGOUT)
      AuthLogger.info('Server logout request completed')
    } catch (error) {
      AuthLogger.warn('Server logout failed, but continuing with cleanup:', error)
      // Don't throw - we still want to clean up locally
    } finally {
      // Always clean up local state regardless of server response
      authSessionStorageService.clearSession()
      this.loginStatus = false
      this.lastIntegrityCheck = 0
      
      // Notify auth state change
      this.notifyAuthStateChange()
      
      AuthLogger.info('Logout completed')
    }
  }

  /**
   * Get current session information
   */
  async getSession(): Promise<GetSessionResponseModel | null> {
    try {
      const response: AxiosResponse<GetSessionResponseModel> = await axiosInstance.get(
        ControllerEndpoint.SessionControllerEndpoint.SESSION,
      )
      return response.data
    } catch (error) {
      AuthLogger.error('Failed to get session', error)
      return null
    }
  }

  /**
   * Enhanced session timing information
   */
  getSessionTiming(): { timeLeft: number; isExpired: boolean; percentLeft: number } {
    const loginTime = authSessionStorageService.getLoginTime()
    const sessionDuration = 30 * 60 * 1000 // 30 minutes in milliseconds
    
    if (!loginTime) {
      return { timeLeft: 0, isExpired: true, percentLeft: 0 }
    }
    
    const now = Date.now()
    const elapsed = now - loginTime
    const timeLeft = Math.max(0, sessionDuration - elapsed)
    const isExpired = timeLeft <= 0
    const percentLeft = Math.max(0, (timeLeft / sessionDuration) * 100)
    
    return { timeLeft, isExpired, percentLeft }
  }

  /**
   * Register callback for auth state changes
   */
  onAuthStateChange(callback: (isAuthenticated: boolean) => void): () => void {
    this.authStateCallbacks.push(callback)
    
    // Return unsubscribe function
    return () => {
      const index = this.authStateCallbacks.indexOf(callback)
      if (index > -1) {
        this.authStateCallbacks.splice(index, 1)
      }
    }
  }

  /**
   * Notify all callbacks of auth state change
   */
  private notifyAuthStateChange(): void {
    const isAuthenticated = this.loginStatus && Boolean(authSessionStorageService.getSession())
    this.authStateCallbacks.forEach(callback => {
      try {
        callback(isAuthenticated)
      } catch (error) {
        AuthLogger.error('Error in auth state callback:', error)
      }
    })
  }

  /**
   * Enhanced session state refresh
   */
  async refreshAuthState(): Promise<boolean> {
    AuthLogger.debug('Refreshing auth state...')
    
    // First check if we have a valid session locally
    const isAuth = await this.isAuthenticated()
    
    if (isAuth) {
      // If we have a session, check if it needs refreshing
      const timing = this.getSessionTiming()
      
      if (timing.isExpired) {
        AuthLogger.warn('Session has expired during refresh check')
        this.loginStatus = false
        this.notifyAuthStateChange()
        return false
      }
      
      // Session is still valid
      AuthLogger.debug('Auth state refresh completed', { 
        isAuthenticated: isAuth,
        timeLeft: Math.round(timing.timeLeft / 60000)
      })
    }
    
    // Notify callbacks of potential state change
    this.notifyAuthStateChange()
    
    return isAuth
  }

  /**
   * Simplified methods for compatibility
   */
  async refreshSession(): Promise<boolean> {
    return this.refreshAuthState()
  }

  /**
   * Ensures a valid session exists, creating one if necessary
   */
  private async ensureSessionExists(): Promise<void> {
    AuthLogger.debug('Ensuring session exists...', undefined, DEBUG_SESSION)
    
    const sessionData = authSessionStorageService.getAllSessionData()
    AuthLogger.sessionData('Current session state', sessionData)
    
    // If we have all required components, we're good
    if (sessionData.sessionId && sessionData.publicKey && sessionData.secretKey) {
      AuthLogger.debug('Session already exists and is complete', undefined, DEBUG_SESSION)
      return
    }
    
    // Clear any partial session data
    if (sessionData.sessionId || sessionData.publicKey || sessionData.secretKey) {
      AuthLogger.warn('Partial session data found, clearing and creating new session')
      authSessionStorageService.clearSession()
    }
    
    // Create a new session
    AuthLogger.info('Creating new session...')
    const sessionCreated = await this.createSession()
    if (!sessionCreated) {
      throw new Error('Failed to create session')
    }
    
    AuthLogger.info('Session creation completed')
  }

  /**
   * Create a new session with comprehensive error handling
   */
  async createSession(): Promise<boolean> {
    AuthLogger.info('Creating new session...')
    
    try {
      // Clear any existing session data
      authSessionStorageService.clearSession()
      
      // Generate RSA key pair
      AuthLogger.debug('Generating RSA key pair...', undefined, DEBUG_SESSION)
      const keyPair = await rsaService.generateRsaKeyPair()
      authSessionStorageService.setClientPrivateKey(keyPair.privateKey)
      
      // Export and encode public key
      AuthLogger.debug('Exporting public key...', undefined, DEBUG_SESSION)
      const exportedPublicKey = await rsaService.exportPublicKey(keyPair.publicKey)
      const hexKey = encodeString(exportedPublicKey)
      
      AuthLogger.debug('Sending session creation request...', undefined, DEBUG_SESSION)
      const response = await axiosInstance.post<CreateNewSessionResponseModel>(
        ControllerEndpoint.SessionControllerEndpoint.NEWSESSIONS,
        { key: hexKey },
      )
      
      const createSessionResponse = response.data
      AuthLogger.debug('Received session creation response', undefined, DEBUG_SESSION)
      
      // Decrypt and store keys
      AuthLogger.debug('Decrypting and storing keys...', undefined, DEBUG_SESSION)
      await this.decryptAndSetSecretKey(createSessionResponse)
      await this.decryptAndSetPublicKey(createSessionResponse)
      
      // Verify session creation (only in debug mode)
      if (DEBUG_SESSION) {
        const finalSessionData = authSessionStorageService.getAllSessionData()
        AuthLogger.sessionData('Session creation verification', finalSessionData)
      }
      
      const sessionData = authSessionStorageService.getAllSessionData()
      if (!sessionData.publicKey || !sessionData.secretKey) {
        throw new Error('Session creation incomplete')
      }
      
      AuthLogger.info('Session creation completed successfully')
      return true
    } catch (error) {
      AuthLogger.error('Session creation failed', error)
      authSessionStorageService.clearSession()
      return false
    }
  }

  /**
   * Helper method to encrypt password and prepare login request
   */
  private async encryptPasswordAndPrepareLoginReq(
    password: string,
    publicKey: CryptoKey,
    username: string,
  ): Promise<{ userName: string; password: string }> {
    const encryptedPassword = await rsaService.encrypt(password, publicKey)
    return { userName: username, password: encryptedPassword }
  }

  /**
   * Decrypt and set public key from session response
   */
  private async decryptAndSetPublicKey(
    createSessionResponse: CreateNewSessionResponseModel,
  ): Promise<string> {
    const storedSecretKey = authSessionStorageService.getSecretKey()
    if (!storedSecretKey) {
      throw new Error('Secret key not available')
    }
    
    const cryptoKey = await aesService.loadSecretKey(storedSecretKey)
    const publicKey = await aesService.decrypt(createSessionResponse.keyOne, cryptoKey)
    
    await authSessionStorageService.setPublicKey(publicKey)
    AuthLogger.debug('Public key decrypted and stored', undefined, DEBUG_SESSION)
    
    return publicKey
  }

  /**
   * Decrypt and set secret key from session response
   */
  private async decryptAndSetSecretKey(
    createSessionResponse: CreateNewSessionResponseModel,
  ): Promise<void> {
    const clientPrivateKey = authSessionStorageService.getClientPrivateKey()
    if (!clientPrivateKey) {
      throw new Error('Client private key not available')
    }
    
    const decryptedAESKey = await rsaService.decrypt(
      createSessionResponse.keyTwo,
      clientPrivateKey,
    )
    
    await authSessionStorageService.setSecretKey(decryptedAESKey)
    authSessionStorageService.setClientPrivateKey(undefined)
    AuthLogger.debug('Secret key decrypted and stored', undefined, DEBUG_SESSION)
  }
}

export const authService = new OptimizedAuthService()